import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShieldCheck, Fingerprint, Send, Receipt } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Btn, Field } from "@/components/shared";
import { homeFor } from "@/nav";
import { MfaStep } from "@/components/MfaStep";
import Logo, { ShubhPayMark } from "@/components/Logo";

const BG = "https://images.unsplash.com/photo-1556742521-9713bf272865?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const [mfa, setMfa] = useState(null);
  const nav = useNavigate();
  const { key } = useParams();
  const [reason] = useState(() => { const r = sessionStorage.getItem("np_logout_reason"); sessionStorage.removeItem("np_logout_reason"); return r; });
  const [f, setF] = useState({ login: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const done = (u) => {
    if (key) localStorage.setItem("np_admin_path", `/secure/${key}`);
    toast.success(`Welcome, ${u.name}`);
    nav(homeFor(u.role));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await login(f.login, f.password, key);
      if (r.mfa) return setMfa(r.mfa);
      done(r);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#070E20] lg:block">
        <img src={BG} alt="Retailer payments" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070E20] via-[#070E20]/40 to-transparent" />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <Logo dark />
          <div className="max-w-md">
            <span className="block overflow-hidden"><motion.span className="block font-display text-4xl font-extrabold leading-tight tracking-tight" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1], delay: 0.1 }}>Your shop's complete</motion.span></span>
            <span className="block overflow-hidden"><motion.span className="block font-display text-4xl font-extrabold leading-tight tracking-tight" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1], delay: 0.22 }}>digital banking <span className="text-emerald-400">counter.</span></motion.span></span>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }} className="mt-4 text-slate-300">Recharge, BBPS, money transfer, AEPS and payouts — one wallet, one login.</motion.p>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.58 }} className="mt-8 grid grid-cols-2 gap-3 text-sm">
              {[[Send, "Instant DMT"], [Fingerprint, "AEPS Withdrawals"], [Receipt, "BBPS Bills"], [ShieldCheck, "MPIN Secured"]].map(([I, t]) => (
                <div key={t} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 backdrop-blur"><I className="h-4 w-4 text-emerald-400" />{t}</div>
              ))}
            </motion.div>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">RBI-compliant · NPCI certified</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        {mfa ? <MfaStep mfa={mfa} onVerify={async (c, code) => done(await verifyMfa(c, code))} onCancel={() => { setMfa(null); setF({ ...f, password: "" }); }} /> : (
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden"><Logo mark="h-9 w-9" /></div>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight lg:mt-0">{key ? "Secure sign in" : "Sign in"}</h2>
          {reason && <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="logout-reason">{reason}</div>}
          <p className="mt-1 text-sm text-slate-500">Use your registered mobile number or login ID.</p>
          <div className="mt-8 space-y-4">
            <Field label="Mobile / Login ID">
              <input className="np-input" value={f.login} onChange={(e) => setF({ ...f, login: e.target.value })} placeholder="9876543210 or RT1001" data-testid="login-input" required />
            </Field>
            <Field label="Password">
              <input type="password" className="np-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="password-input" required />
            </Field>
            {!key && <div className="text-right"><Link to="/forgot-password" className="text-xs font-semibold text-[#0D1B3E] hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div>}
            {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</div>}
            <Btn type="submit" loading={loading} className="w-full" data-testid="login-submit-button">Login</Btn>
            {!key && <p className="pt-2 text-center text-xs text-slate-400">New to ShubhPay? <Link to="/" className="font-semibold text-emerald-600 hover:underline">Become a retailer</Link></p>}
          </div>
        </form>)}
      </div>
    </div>
  );
}
